/* eslint-disable react/require-default-props */
import {
    Button, Drawer, Dropdown, Grid, Menu, Space,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LogoutOutlined, MenuOutlined, UserOutlined } from '@ant-design/icons';
import { useAuthenticate } from '../../hooks/api/auth/useAuthenticate';
import { useMenu } from '../../hooks/api/useMenu';
import { IMenuItem } from '../../types';
import { useUser } from '../../hooks/useUser';
import { useSubUser } from '../../hooks/useSubUser';
import ModalEdit from '../modal-edit';
import { useModalEdit } from '../../hooks/table/useModalEdit';
import PusherService from '../../services/pusher';

interface IAppMenuProps {
    initIgnore?: boolean;
}

const { useBreakpoint } = Grid;

const AppMenu = ({ initIgnore }: IAppMenuProps) => {
    const [showDrawer, setShowDrawer] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const { logout } = useAuthenticate();
    const menu = useMenu();
    const { objectName } = useParams();
    const navigate = useNavigate();
    const user = useUser(false);
    const { xl } = useBreakpoint();
    const subUser = useSubUser();
    const modal = useModalEdit();
    const pusher = useRef(new PusherService());

    const navigateToFirstMenuPage = () => {
        let found = false;
        let i = 0;
        while (!found) {
            const { path } = menu.menuData[i];
            if (typeof path === 'string') {
                found = true;
                navigate(path);
            }
            i += 1;
        }
    };

    const onTitleClick = async () => {
        if (menu.action instanceof Object) {
            if (typeof menu.action.layoutLink === 'string') {
                modal.getLayout({ layoutLink: menu.action.layoutLink });
                modal.show();
            }
        }
    };

    const openSocket = () => {
        if (user.info?.id) {
            pusher.current.open(`user-${user.info?.id}`);
            pusher.current.registerMessageListener('crm-mode-changed', () => {
                window.location.reload();
            });
        }
    };

    useEffect(() => {
        menu.getMenuData();
    }, []);

    useEffect(() => {
        if (typeof objectName === 'undefined' && menu.menuData.length > 0 && !initIgnore) {
            navigateToFirstMenuPage();
        }
    }, [objectName, menu.menuData]);

    useEffect(() => {
        setSelectedKeys(typeof objectName === 'string' ? [`.$${objectName}`] : []);
    }, [objectName]);

    useEffect(() => {
        openSocket();
        return () => {
            pusher.current.close();
        };
    }, [user.info?.id]);

    const renderMenu = (data: IMenuItem[]) => React.Children.toArray(data.map((item) => {
        if (Array.isArray(item.items)) {
            return (
                <Menu.SubMenu key={item.key} title={item.title}>
                    {renderMenu(item.items)}
                </Menu.SubMenu>
            );
        }
        if (typeof item.path === 'string') {
            return (
                <Menu.Item key={item.key}>
                    <Link to={item.path}>
                        {item.title}
                    </Link>
                </Menu.Item>
            );
        }
        return null;
    }));

    if (xl) {
        return (
            <>
                <img alt="Logo" src="/assets/logo.png" />
                <Menu mode="horizontal" theme="dark" selectedKeys={selectedKeys}>
                    {renderMenu(menu.menuData)}
                    {subUser.info instanceof Object && user.info instanceof Object && subUser.info.id !== user.info?.id && (
                        <Menu.Item className="ant-menu-item-active menu-sub-user-block" style={{ marginLeft: 10 }}>
                            <Space size="middle">
                                <span>{subUser.info?.fio}</span>
                                <Button
                                    className="button-vertical-content"
                                    icon={<LogoutOutlined />}
                                    title="Выйти"
                                    size="small"
                                    onClick={subUser.logout}
                                >
                                    Выйти
                                </Button>
                            </Space>
                        </Menu.Item>
                    )}
                </Menu>
                {(!menu.loading || menu.menuData.length > 0) && (
                    <Dropdown.Button
                        overlay={(
                            <Menu>
                                <Menu.Item onClick={logout} icon={<LogoutOutlined />}>
                                    Выйти
                                </Menu.Item>
                            </Menu>
                        )}
                        icon={<UserOutlined />}
                        onClick={onTitleClick}
                    >
                        {typeof user.info?.fio === 'string' && <span>{user.info?.fio}</span>}
                    </Dropdown.Button>
                )}
                <ModalEdit hook={modal} />
            </>
        );
    }

    return (
        <>
            <div className="d-flex flex-1 align-items-center justify-content-between">
                <img alt="Logo" src="/assets/logo.png" style={{ height: 50 }} />
                <Button icon={<MenuOutlined />} onClick={() => setShowDrawer(true)} type="primary" />
            </div>
            <Drawer placement="left" visible={showDrawer} onClose={() => setShowDrawer(false)}>
                <Menu mode="inline" selectedKeys={selectedKeys}>
                    <Menu.Item>
                        <h3 className="mb-0">Меню</h3>
                    </Menu.Item>
                    <Menu.Divider />
                    {renderMenu(menu.menuData)}
                    {subUser.info instanceof Object && user.info instanceof Object && subUser.info.id !== user.info?.id && (
                        <Menu.SubMenu title={`${subUser.info?.fio || 'Пользователь'} (менеджер)`} icon={<UserOutlined />}>
                            <Menu.Item onClick={subUser.logout}>
                                Выйти
                            </Menu.Item>
                        </Menu.SubMenu>
                    )}
                    <Menu.Divider />
                    <Menu.SubMenu title={user.info?.fio || 'Пользователь'} icon={<UserOutlined />} onTitleClick={onTitleClick}>
                        <Menu.Item onClick={logout}>
                            Выйти
                        </Menu.Item>
                    </Menu.SubMenu>
                </Menu>
            </Drawer>
            <ModalEdit hook={modal} />
        </>
    );
};

export default AppMenu;
